import type { ApiResponse } from '@msk/shared';
import { getAuthToken, setAuthToken, clearAuthToken } from './authToken';
import { toast } from '@/components/ui/toast';
import i18n from '@/i18n';

// ── Typed error ────────────────────────────────────────────────────────────
// A proper error type means callers can branch on .status / .code without
// parsing error messages.
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    /** Sprint 6 — the parsed error body, for coded responses that carry data
     *  (e.g. 409 NO_TREATMENT_FREQUENCY names the clinic's default preset). */
    public readonly data?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function getAuthHeader(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── P1-15 — silent token refresh ───────────────────────────────────────────
// Access tokens now live 1h. On a 401 the api wrapper calls
// POST /api/auth/refresh (using the httpOnly refresh cookie) and retries
// the original request once with the new access token. A shared
// in-flight promise means 30 concurrent requests that all 401 at the
// same moment only trigger ONE refresh.
let pendingRefresh: Promise<string | null> | null = null;

/**
 * Endpoints that must NEVER trigger silent-401 recovery.
 *  - /api/auth/refresh: refreshing the refresh call is an infinite loop.
 *  - /api/auth/logout:  a logout that 401s has ALREADY succeeded client-side.
 *    Refreshing in order to destroy the session used to mint and store a fresh
 *    ~1h access token AFTER logout() had cleared storage; since access JWTs are
 *    stateless (logout revokes only refresh tokens), that token stayed LIVE and
 *    a page reload could undo an 8-hour inactivity logout. Observed 9× in prod.
 *  - /api/version: Set D W2 N2. The stale-bundle poll is deliberately
 *    UNAUTHENTICATED and runs every 5 minutes in every open tab. Its whole
 *    audience is clients that may be stale AND expired, so letting it drive
 *    silent refreshes would turn a version check into background auth churn.
 */
const NO_SILENT_REFRESH_PATHS = ['/api/auth/refresh', '/api/auth/logout', '/api/version'] as const;

function skipsSilentRefresh(url: string): boolean {
  return NO_SILENT_REFRESH_PATHS.some((p) => url.endsWith(p));
}

/** Shared single-flight refresh. Exported so useAiStream can reuse it rather
 *  than hard-redirecting on the first 401 after the 1h token expiry. */
export async function silentRefresh(): Promise<string | null> {
  if (pendingRefresh) return pendingRefresh;
  pendingRefresh = (async () => {
    try {
      const resp = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',              // send httpOnly refresh cookie
        headers: { 'Content-Type': 'application/json' },
        // include a body fallback for legacy flows — backend accepts both
        body: JSON.stringify({}),
      });
      if (!resp.ok) return null;
      const json = (await resp.json()) as { success?: boolean; data?: { token?: string; user?: unknown } };
      if (!json.success || !json.data?.token) return null;
      setAuthToken(json.data.token);
      if (json.data.user) localStorage.setItem('msk_user', JSON.stringify(json.data.user));
      return json.data.token;
    } catch {
      return null;
    } finally {
      // Release the singleton so a subsequent 401 can trigger a fresh attempt.
      pendingRefresh = null;
    }
  })();
  return pendingRefresh;
}

/**
 * Read a response body safely. If the server returned JSON, parse it.
 * If it returned anything else (e.g. express-rate-limit's plain-text
 * "Too many requests, please try again later."), return the raw text so
 * the caller can render it without crashing.
 */
async function readBody(response: Response): Promise<{ json: unknown | null; text: string }> {
  const ct = response.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    try {
      const json = await response.json();
      return { json, text: '' };
    } catch {
      return { json: null, text: '' };
    }
  }
  const text = await response.text().catch(() => '');
  return { json: null, text };
}

async function handleNonOk(response: Response): Promise<never> {
  const { json, text } = await readBody(response);
  const errData = (json as { error?: string; code?: string; message?: string } | null) ?? null;

  // 429 — Too Many Requests. Never parse as JSON-with-data; never logout.
  if (response.status === 429) {
    throw new ApiError(
      'The server is busy. Please wait a moment and try again.',
      429,
      'RATE_LIMITED',
    );
  }

  // 401 — auth token rejected. Clear local storage and bounce to login.
  //       This is the ONLY status that triggers logout. A toast fires
  //       first so the user sees why they're being redirected, instead of
  //       landing on /login with no explanation (Nikos hit this 2026-04-21
  //       during AI Paste — proof/post-v2/session-ux/).
  //
  // Bundle 2a.1b — when the page is in print mode (`?print=1`),
  // skip the redirect-to-login and the localStorage purge. The
  // headless tab uses a short-lived pdf_renderer URL token; if any
  // single API call 401s (e.g. /auth/me, which doesn't accept
  // pdf_renderer audience), we still want the rest of the print-
  // ready signal coordinator's pending tasks to fire so Puppeteer
  // can capture. The 401 still surfaces to the caller via the
  // thrown ApiError — they can choose to gate their own
  // completePrintTask call accordingly.
  if (response.status === 401) {
    const isPrintTab = typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).get('print') === '1';
    if (!isPrintTab) {
      if (!window.location.pathname.includes('/login')) {
        toast.error(
          i18n.t('session.expired.toast', 'Your session expired. Please log in again.'),
          5000,
        );
      }
      clearAuthToken();
      localStorage.removeItem('msk_user');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    throw new ApiError(errData?.error ?? 'Your session has expired. Please sign in again.', 401, 'UNAUTHORIZED');
  }

  // Everything else — surface a typed error. Do NOT logout on 500, network errors, etc.
  const message =
    errData?.error ??
    errData?.message ??
    (text && text.length < 400 ? text : null) ??
    `Request failed with status ${response.status}`;
  throw new ApiError(message, response.status, errData?.code, (errData ?? undefined) as Record<string, unknown> | undefined);
}

// ── In-flight tracker for global progress bar (QA UX FIX-20) ───────────────
// A tiny event bus the <GlobalProgressBar /> component can subscribe to.
// Anything that calls api.get/post/etc automatically bumps the counter,
// so we get a progress bar for free without touching every caller.
let inFlightCount = 0;
const inFlightListeners = new Set<(n: number) => void>();
function notifyInFlight(): void {
  for (const l of inFlightListeners) l(inFlightCount);
}
export function subscribeInFlight(listener: (n: number) => void): () => void {
  inFlightListeners.add(listener);
  listener(inFlightCount);
  return () => { inFlightListeners.delete(listener); };
}
function track<T>(p: Promise<T>): Promise<T> {
  inFlightCount += 1;
  notifyInFlight();
  return p.finally(() => {
    inFlightCount = Math.max(0, inFlightCount - 1);
    notifyInFlight();
  });
}

import {
  assessmentIdFromDraftUrl,
  isAssessmentDraftLocked,
  markAssessmentLocked,
  DRAFT_LOCKED_MESSAGE_EN,
} from './draftLock';

// ── Autosave tracker (QA UX FIX-21) ────────────────────────────────────────
// Any request whose URL matches /draft or /save-draft is considered an
// autosave. We expose a tiny observable so an <AutosaveIndicator /> can
// render "Saving…", "Saved", or "Save failed" without every caller
// touching the indicator by hand.
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'locked';
interface SaveState {
  status: SaveStatus;
  lastSavedAt: number | null;
  errorMessage: string | null;
}
let saveState: SaveState = { status: 'idle', lastSavedAt: null, errorMessage: null };
const saveListeners = new Set<(s: SaveState) => void>();
function notifySave(): void {
  for (const l of saveListeners) l(saveState);
}
export function subscribeSave(listener: (s: SaveState) => void): () => void {
  saveListeners.add(listener);
  listener(saveState);
  return () => { saveListeners.delete(listener); };
}
function isAutosaveUrl(url: string, method: string): boolean {
  if (method === 'GET' || method === 'DELETE') return false;
  return /\/(save-draft|draft)(\?|$)/.test(url);
}
function markSaving(): void {
  saveState = { status: 'saving', lastSavedAt: saveState.lastSavedAt, errorMessage: null };
  notifySave();
}
function markSaved(): void {
  saveState = { status: 'saved', lastSavedAt: Date.now(), errorMessage: null };
  notifySave();
}
function markSaveLocked(): void {
  // Set F W-C1 — a locked record is NOT an error state. obs8 CF-4: the old code
  // turned every non-ok draft response into "Save failed (409)", so finalizing an
  // assessment produced a stream of red failures for something that had not failed.
  saveState = { status: 'locked', lastSavedAt: saveState.lastSavedAt, errorMessage: DRAFT_LOCKED_MESSAGE_EN };
  notifySave();
}

function markSaveError(msg: string): void {
  saveState = { status: 'error', lastSavedAt: saveState.lastSavedAt, errorMessage: msg };
  notifySave();
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const url = path.startsWith('/api') ? path : `/api${path}`;
  const method = (options.method ?? 'GET').toUpperCase();
  const isAutosave = isAutosaveUrl(url, method);

  // Set F W-C1 — THE CHOKE POINT. A draft write to a known-finalized assessment
  // is not sent at all. This is what actually stops obs8 CF-4's storm: nine
  // components PATCH /draft directly and never saw useAssessmentWizard's guards,
  // so guarding here covers all of them at once (and any future caller).
  // Suppression is silent and NEUTRAL — nothing failed, so nothing is reported
  // as a failure.
  if (isAutosave && method !== 'GET') {
    const draftId = assessmentIdFromDraftUrl(url);
    if (isAssessmentDraftLocked(draftId)) {
      markSaveLocked();
      return { success: true, data: null as unknown as T };
    }
  }

  if (isAutosave) markSaving();

  const doFetch = () => track(
    fetch(url, {
      ...options,
      credentials: 'include', // P1-15: send the mski_rt cookie on refresh
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
        ...options.headers,
      },
    }),
  );

  let response: Response;
  try {
    response = await doFetch();
  } catch (e) {
    // Network failure — do NOT logout, let the caller handle it.
    if (isAutosave) markSaveError('Network error');
    if (e instanceof ApiError) throw e;
    throw new ApiError('Network error. Please check your connection and try again.', 0, 'NETWORK');
  }

  // P1-15: silent 401 recovery. Skip for the refresh endpoint itself to
  // avoid infinite loops. Retry exactly once per request.
  if (response.status === 401 && !skipsSilentRefresh(url)) {
    const newToken = await silentRefresh();
    if (newToken) {
      try {
        response = await doFetch(); // getAuthHeader() reads the freshly-stored token
      } catch {
        // fall through to the existing non-ok handler below
      }
    }
  }

  if (!response.ok) {
    // Set F W-C1 — a 409 on a draft write means the server considers this
    // assessment finalized (another tab, a mid-wizard ddx-review finalize, or a
    // save already in flight when finalize landed). Remember it so no further
    // write is attempted, and report it NEUTRALLY: "Save failed" is untrue here.
    if (isAutosave && response.status === 409) {
      const draftId = assessmentIdFromDraftUrl(url);
      if (draftId) markAssessmentLocked(draftId);
      markSaveLocked();
      await handleNonOk(response);
    }
    if (isAutosave) markSaveError(`Save failed (${response.status})`);
    await handleNonOk(response);
  }

  const { json } = await readBody(response);
  if (isAutosave) markSaved();
  return (json as ApiResponse<T>) ?? ({ success: true, data: null as unknown as T });
}

async function uploadRequest<T>(
  path: string,
  formData: FormData,
): Promise<ApiResponse<T>> {
  const url = path.startsWith('/api') ? path : `/api${path}`;

  let response: Response;
  try {
    response = await track(
      fetch(url, {
        method: 'POST',
        headers: { ...getAuthHeader() },
        body: formData,
      }),
    );
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError('Network error during upload. Please try again.', 0, 'NETWORK');
  }

  if (!response.ok) {
    await handleNonOk(response);
  }

  const { json } = await readBody(response);
  return (json as ApiResponse<T>) ?? ({ success: true, data: null as unknown as T });
}

/**
 * fix(auth-safe-fetches) BR-09 — binary/raw responses (PDF downloads, server
 * HTML) with the SAME auth + silent-refresh + retry-once semantics as request().
 * Raw fetch call sites attached tokens by hand and broke silently after the
 * 1-hour expiry; this is the one sanctioned path for non-JSON bodies.
 * Throws ApiError on non-ok; returns the raw Blob (use .text() for HTML).
 */
async function blobRequest(path: string, options: RequestInit = {}): Promise<Blob> {
  const url = path.startsWith('/api') ? path : `/api${path}`;
  const doFetch = () => track(
    fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...getAuthHeader(),
        ...options.headers,
      },
    }),
  );
  let response = await doFetch();
  if (response.status === 401 && !skipsSilentRefresh(url)) {
    const newToken = await silentRefresh();
    if (newToken) response = await doFetch();
  }
  if (!response.ok) {
    throw new ApiError(`Request failed (${response.status})`, response.status);
  }
  return response.blob();
}

export const api = {
  get: <T>(path: string): Promise<ApiResponse<T>> =>
    request<T>(path),
  post: <T>(path: string, body: unknown): Promise<ApiResponse<T>> =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown): Promise<ApiResponse<T>> =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown): Promise<ApiResponse<T>> =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string): Promise<ApiResponse<T>> =>
    request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData): Promise<ApiResponse<T>> =>
    uploadRequest<T>(path, formData),
  blob: (path: string, options: RequestInit = {}): Promise<Blob> =>
    blobRequest(path, options),
};
